import { contactStrings } from '@data/contacts';
import { invoiceStrings } from '@data/invoice';
import { country } from '@models/Country';
import { D2DError } from '@utils/errors';
import { foreignKeyField, requiredStringField, stringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

export const invoiceCurrency = t.union([t.literal('USD'), t.literal('JPY')]);

export const invoiceStatus = t.union([
  t.literal('作成中'),
  t.literal('修正中'),
  t.literal('発行済'),
]);

export const invoiceLanguage = t.union([t.literal('ENGLISH'), t.literal('日本語')]);

export type InvoiceStatus = t.TypeOf<typeof invoiceStatus>;

export const invoicePaymentStatus = t.union([t.literal('済'), t.literal('未')]);

export type InvoicePaymentStatus = t.TypeOf<typeof invoicePaymentStatus>;

export const invoiceBase = t.type({
  house_bl_number: t.string,
  charge_date: t.string,
  client_id: t.number,
  client_code: t.string,
  client_name: t.string,
  client_name_en: t.string,
  client_zipcode: t.string,
  client_address1: t.string,
  client_address2: t.string,
  payer_mark: country,

  invoice_no: t.string, // kikan INV NO.
  job_code: t.string, // kikan 台帳番号
  charge_pay_day: t.string, // kikan 支払期限

  vessel_voyage: t.string,
  transport_route: t.string,
  ata: t.string,
  quantity_packages: t.string,
  customer_no: t.string,
  conversion_rate: t.string,
  conversion_rate_date: t.string,
  language: invoiceLanguage,
  invoice_status: invoiceStatus,
  salesperson_name: t.string,
});

export type InvoiceBase = t.TypeOf<typeof invoiceBase>;

export const invoiceBaseInitialValues: InvoiceBase = {
  house_bl_number: '',
  charge_date: '',
  client_id: undefined as any,
  client_code: '',
  client_name: '',
  client_name_en: '',
  client_zipcode: '',
  client_address1: '',
  client_address2: '',
  payer_mark: 'jp',

  invoice_no: '',
  job_code: '',
  charge_pay_day: '',

  vessel_voyage: '',
  transport_route: '',
  ata: '',
  quantity_packages: '',
  customer_no: '',
  conversion_rate: '',
  conversion_rate_date: '',
  language: '日本語',
  invoice_status: '作成中',
  salesperson_name: '',
};

export const invoice = t.type({
  ...invoiceBase.props,
  id: t.number,
  visit_id: t.string,

  // FC-managed fields
  charge_no: h.omittable(t.string), // FC Primary Key
  payment_date: h.omittable(t.string),
  finish_date: h.omittable(t.string),
  payment_status: invoicePaymentStatus,

  // D2D-managed state fields
  d2d_charge_no: t.string,
  review_time: h.omittable(t.string), // Time of 修正中
  send_time: h.omittable(t.string), // Time of 発行済
  total_price: t.string,
  total_price_currency: invoiceCurrency,
  booking_inquiry: t.number,
  member_salesperson_name: h.omittable(t.string),
  member_salesperson_last_name: h.omittable(t.string),
  salesperson_japan_last_name: h.omittable(t.string),
  shipper_name: h.omittable(t.string),
  consignee_name: h.omittable(t.string),
  preferred_arrival_date: h.omittable(t.string),
  quantity: h.omittable(t.number),
  receiver_company_name: h.omittable(t.string),
  service_name: h.omittable(t.string),

  created_at: t.string,
  updated_at: t.string,
});

export type Invoice = t.TypeOf<typeof invoice>;

export const invoiceJobCodeForm = t.type({
  house_bl_number: t.string,
  charge_date: requiredStringField(invoiceStrings.charge_date),
  client_id: foreignKeyField(invoiceStrings.client_id),
  client_name: requiredStringField(contactStrings.company_name),
  client_name_en: stringField(contactStrings.company_name),
  client_zipcode: requiredStringField(contactStrings.zip_code),
  client_address1: requiredStringField(contactStrings.address1),
  client_address2: requiredStringField(contactStrings.address2),
});

export type InvoiceJobCodeForm = t.TypeOf<typeof invoiceJobCodeForm>;

export const invoiceInfoForm = t.type({
  invoice_no: requiredStringField(invoiceStrings.invoice_no),
  job_code: requiredStringField(invoiceStrings.job_code),
  charge_pay_day: requiredStringField(invoiceStrings.charge_pay_day),
  vessel_voyage: requiredStringField(invoiceStrings.vessel_voyage),
  transport_route: requiredStringField(invoiceStrings.transport_route),
  ata: requiredStringField(invoiceStrings.ata),
  quantity_packages: requiredStringField(invoiceStrings.quantity_packages),
  customer_no: stringField(invoiceStrings.customer_no),
  conversion_rate_date: requiredStringField(invoiceStrings.conversion_rate),
  language: requiredStringField(invoiceStrings.language),
});

export const invoicePostBody = t.type({
  ...invoiceBase.props,
  booking_inquiry: t.number,
  invoice_id_list: t.array(t.number),
});

export type InvoicePostBody = t.TypeOf<typeof invoicePostBody>;

export const invoicePatchBody = t.partial({
  ...invoiceBase.props,
  invoice_id_list: t.array(t.number),
  invoice_status: invoiceStatus,
});

export type InvoicePatchBody = t.TypeOf<typeof invoicePatchBody>;

export const invoicePages = t.type({
  count: t.number,
  next: h.omittable(t.string),
  previous: h.omittable(t.string),
  results: t.array(invoice),
});

export type InvoiceDefaultValue = t.TypeOf<typeof invoiceDefaultValue>;

export const invoiceDefaultValue = t.type({
  house_bl_number: h.omittable(t.string),
  request_date: h.omittable(t.string),
  vessel_voyage: h.omittable(t.string),
  transport_route: h.omittable(t.string),
  ata: h.omittable(t.string),
  quantity_packages: h.omittable(t.string),
  salesperson_name: h.omittable(t.string),
  conversion_rate: h.omittable(t.string),
  conversion_rate_date: h.omittable(t.string),
  customer_no: h.omittable(t.string),
  charge_date: h.omittable(t.string),
});

export const invoiceErrorMessage = t.type({
  status: h.omittable(t.string),
  message: h.omittable(t.string),
  invoice: h.omittable(invoice),
});

export type InvoiceErrorMessage = t.TypeOf<typeof invoiceErrorMessage>;

export class InvoiceError extends D2DError {
  constructor(public error: InvoiceErrorMessage) {
    super(error.message ?? 'InvoiceError');
  }
}

const strings = createStrings({
  ja: {
    fromTheNewTime: '時間順 新→古',
    fromTheOldTime: '時間順 古→新',
    sendStatus: '発行済',
    creatingStatus: '作成中',
    all: 'ALL',
    payStatus: '済',
    unPayStatus: '未',
  },
});

export const orderingChargeDateOptions = [
  {
    value: '-charge_date',
    label: strings.fromTheNewTime,
  },
  {
    value: 'charge_date',
    label: strings.fromTheOldTime,
  },
];

export const orderingPreferredArrivalDateOptions = [
  {
    value: '-preferred_arrival_date',
    label: strings.fromTheNewTime,
  },
  {
    value: 'preferred_arrival_date',
    label: strings.fromTheOldTime,
  },
];

export const orderingChargePayDayOptions = [
  {
    value: '-charge_pay_day',
    label: strings.fromTheNewTime,
  },
  {
    value: 'charge_pay_day',
    label: strings.fromTheOldTime,
  },
];

export const orderingInvoiceStatusOptions = [
  ...invoiceStatus.types.map((t) => ({
    value: t.value,
    label: t.value,
  })),
  {
    value: '',
    label: 'ALL',
  },
];

export const orderingPaymentStatusOptions = [
  {
    value: '済',
    label: strings.payStatus,
  },
  {
    value: '未',
    label: strings.unPayStatus,
  },
  {
    value: '',
    label: strings.all,
  },
];

export const invoiceStatusOptions = [
  ...invoiceStatus.types.map((t) => ({
    value: t.value,
    label: t.value,
  })),
];
